import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { useEffect, useRef, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCheckoutState,
  useCheckoutToken,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useIsAppleRetailOrder,
  useIsConsumerLendingOrder,
  useIsEmptyCheckoutToken,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, Features, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

// sessionStorage key recording the checkout token the summary-redesign exposure last fired for, so
// the exposure is deduped per checkout (survives refresh, re-fires for a new token). See the hook.
const SUMMARY_EXPOSURE_TOKEN_KEY = 'pmSummaryRedesignExposureToken'

// Fires the `Triggered Experiment` exposure for the Pay Monthly summary redesign when the consumer
// reaches the Pay Monthly SUMMARY page (CASHDAP-3379). The experiment metric is summary-page
// drop-off, and there are drop-off points between login and the summary, so the exposure is anchored
// to the summary VIEW rather than to login. Mounted once on the summary route (`Summary.tsx`).
//
// Gated on `useIsConsumerLendingOrder` — i.e. the consumer has SELECTED Pay Monthly (PCL) and is
// therefore being routed to the PM summary — so only the population that actually sees the
// experiment surface is enrolled. (At login we used PCL-offer PRESENCE because the payment type
// wasn't chosen yet; at the summary the selection is settled, and the routing that picks the
// redesigned vs legacy summary keys off this same signal.) The exposure fires for BOTH arms —
// treatment (variant A, redesigned summary) and control (variant C, legacy summary) — because
// `experiment.active` is true for either enrolled value (see `getExperiment`); non-enrolled users
// (OFF / ERROR) stay inactive and never log.
//
// By summary time, start-checkout and the post-start LD re-identify have completed, so the
// elaborate login-time readiness gating is no longer needed. We route through the standard
// `useExperimentTrigger`, which dedups on the `experiments.activeExperiments` slice (once per
// experiment session) and requires a non-empty checkout token — the conventional exposure path used
// by every other experiment. `useExperiment(..., false)` resolves the variant without auto-firing so
// this hook stays the sole trigger.
export const useTriggerSummaryExperimentExposure = (): void => {
  const experiment = useExperiment('PayMonthlySummaryRedesign', false)
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const { started } = useCheckoutState()
  const ldClient = useLDClient()
  const token = useCheckoutToken()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    // Fire at most once per mount (ref) — primarily a render-loop guard: dispatching the assignment
    // below re-renders this hook (it reads the activeExperiments slice), and the guard stops the
    // effect re-running into a max-update-depth loop (also covers the case where sessionStorage throws).
    if (hasFiredRef.current) {
      return
    }
    // Only the PM summary surfaces the experiment. Apple Retail PCL orders are consumer-lending but
    // render the Apple Retail summary (its branch precedes the PM one in Summary.tsx), so exclude them
    // — mirroring that routing predicate — to avoid enrolling users who never saw the experiment.
    if (isAppleRetailOrder || !isConsumerLendingOrder || !hasFeatureFlagsReady || experiment.loading || !experiment.active || token.length === 0) {
      return
    }
    // The redesign flag can be TARGETED by started.hasOrderHistory. start-checkout updates `started`
    // without clearing `hasFeatureFlagsReady`, and LaunchDarkly re-identifies in a passive effect
    // afterwards — so a fast/express path could fire with the pre-start variant. Wait until the live
    // LD context reflects the started order-history value, so the logged variant is the FINAL
    // post-start one.
    const ldContext = ldClient?.getContext?.() as { hasOrderHistory?: boolean } | undefined
    if (ldContext?.hasOrderHistory !== started?.hasOrderHistory) {
      return
    }
    hasFiredRef.current = true
    // Dedup per CHECKOUT TOKEN, not session-wide. This exposure represents a PM summary-page VIEW, so
    // a second checkout (a new token) in the same tab must re-record it. The fired token is kept in
    // sessionStorage: a manual refresh of the SAME checkout does NOT re-fire, a NEW token DOES, and a
    // new browser session starts clean. We therefore log directly rather than through the
    // `useTriggerExperimentEvent` slice dedup, which keys on `experiments.activeExperiments` — that
    // slice is session-wide and never cleared per checkout, so it would drop the second exposure.
    try {
      if (window.sessionStorage.getItem(SUMMARY_EXPOSURE_TOKEN_KEY) === token) {
        return
      }
      window.sessionStorage.setItem(SUMMARY_EXPOSURE_TOKEN_KEY, token)
    } catch {
      // sessionStorage unavailable (e.g. private mode) — fall through and log once for this mount.
    }
    const { name, value, control } = experiment
    dispatch(triggerExperiment([name, value]))
    logEvent('Triggered Experiment', {
      experimentName: Experiments[name],
      experimentValue: value,
      experimentControl: control,
      experimentFlag: `${name}${value}`,
      exposureTrigger: 'summary',
    })
  }, [isAppleRetailOrder, isConsumerLendingOrder, hasFeatureFlagsReady, started, ldClient, token, experiment, dispatch, logEvent])
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  // Mirrors the routing in LoggedIn.tsx — only users who would actually be sent down the
  // experimental no-payment-method branch should be enrolled, otherwise variant A and control
  // behave identically and dilute the analysis population. `selectPreferredCard` collapses the
  // full saved-card / fallback state into preferredCard.card: it's null only when the user has
  // no preferred and no usable fallback, and non-null in every other case (including the
  // errored-preferred-with-no-fallback edge), so this check matches LoggedIn's
  // `preferredCard?.card !== null` routing guard exactly.
  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

// Symmetric to useIsEligibleForAddPaymentMethodFlowExperiment but for the control arm. Returns
// true iff the user is a variant-C participant who would be routed to the standard
// add-payment-method page in LoggedIn.tsx. Same cohort/design/lending/no-payment-method gates
// as Test — the only difference is the variant check. Also requires `experiment.active` and
// !experiment.loading because `getExperiment` defaults `value` to 'C' while LD is loading
// (`apps/checkout/src/model/experiments.tsx`'s `loading` constant), and we must not enroll
// users whose variant assignment hasn't actually resolved yet.
//
// Background: Amplitude variant-C `Triggered Experiment` events dropped from ~30-60/day to 0
// on 2026-05-06 (the day PR #11859 deployed). The original eligibility hook only ever fires
// the trigger for variant=A users, so it can't have been the source of the pre-2026-05-06
// variant-C events — that means some other implicit mechanism was firing them and stopped.
// Restoring symmetric explicit firing here closes the gap without depending on whatever
// implicit mechanism existed before; the dispatcher's analyticsFired dedup in
// useTriggerExperimentEvent bounds any future double-firing to one event per session.
export const useIsControlForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value, active, loading } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  return active && !loading && value === 'C' && cohort !== Cohort.CashOnly && !isBronteDesignLanguage && !isPaymentMethodEligible && hasNoUsablePaymentMethod
}

const usePi4SwitcherNoPaymentMethodExperimentEligibility = (
  selectedPaymentType?: PaymentType
): { eligible: boolean; experimentValue: ExperimentValue; experimentActive: boolean; experimentLoading: boolean } => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue, active, loading } = experiment
  const isPi4SwitcherSummaryFirstEnabled = useFlag(Features.CHECKOUT_ENABLE_ADD_PAYMENT_METHOD_PM_PI4_SWITCHER_SUMMARY_FIRST, false)
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    isPi4SwitcherSummaryFirstEnabled,
    selectedPaymentType === PaymentType.PBI,
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    isConsumerLendingEligible,
    hasNoUsablePaymentMethod,
  ]

  return {
    eligible: eligibilityConditions.every((condition) => condition),
    experimentValue: addPaymentMethodFlowExperimentValue,
    experimentActive: active,
    experimentLoading: loading,
  }
}

export const useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && (experimentValue === 'A' || experimentValue === 'C')
}

export const useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && experimentValue === 'A'
}

// Returns a callback that fires the Triggered Experiment event for the current add-payment-method
// experiment assignment. Caller is responsible for invoking this only when the user is actually
// routed through a no-payment-method branch (currently from LoggedIn.tsx, gated on requestCompleted +
// hasFeatureFlagsReady). The eligibility hooks above intentionally do NOT auto-fire because
// `preferredCard?.card === null` can be transiently true (e.g. after `resetPreferredPaymentMethodErrors`
// mutates the card field, or before `setCardsForExpiryLimit` runs with a populated `started` payload),
// which previously enrolled users with saved cards and diluted the variant-A pool.
//
// Dispatches synchronously rather than using the `useExperimentTrigger` setState+useEffect
// indirection: callers invoke this immediately before `router.replace`, so the LoggedIn page
// can unmount in the next render and skip a deferred effect. Calling `useTriggerExperimentEvent`
// directly guarantees the Redux dispatch + analytics logEvent happen in the same execution as
// the trigger() invocation.
export const useAddPaymentMethodFlowExperimentTrigger = (): ((customEventProperties?: Record<string, unknown>) => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return (customEventProperties) => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }
}
